import {useMemo} from 'react'
import {
  RemovalZone,
  RemovalZoneCreateCommand,
} from "../../model/removalZone";
import {WoodSpecies} from "../../../../referentiels/woodSpecies";
import {
  TransformationRoles,
  useExtendedAuth,
  useGetOrganizationRefs
} from "@tracabois/connect";
import {DropZonePicture} from "@komune-io/g2-fs";
import {FormComposable, FormComposableState, FormComposableField} from "@komune-io/g2-composable";
import {Stack} from '@mui/material';
import { Department } from "@tracabois/domain";
import { Commune } from '../../../../referentiels/commune';
import { RemovalZoneCertification } from '../../../../referentiels/removalZoneCertification';
import { DropPictureProps } from '@komune-io/g2-components';

export interface RemovalZoneFormProps {
  item?: Partial<RemovalZone>
  readonly?: boolean
  woodSpecies: WoodSpecies[]
  departments: Department[]
  communes: Commune[]
  removalZoneCertifications: RemovalZoneCertification[]
  formState: FormComposableState
  imageDropZone: DropPictureProps
  readonlyOrganization?: boolean
}

export const RemovalZoneForm = (props: RemovalZoneFormProps) => {
  const {
    item,
    readonly = false,
    woodSpecies,
    departments,
    communes,
    removalZoneCertifications,
    formState,
    readonlyOrganization = false,
    imageDropZone
  } = props
  const {service} = useExtendedAuth()

  const getOrganizationRefs = useGetOrganizationRefs({
    roles: TransformationRoles
  })

  const communeOptions = useMemo(() =>
    communes.map(c => ({key: c.id, label: `${c.name} (${c.codeInsee})`})),
    [communes]
  )

  const detailsFields = useMemo(
    (): FormComposableField[] => [
      {
        key: 'name',
        name: 'name',
        type: 'textField',
        label: 'Nom de la forêt',
        defaultValue: item?.name,
        params: {
          disabled: readonly
        },
        validator: (value?: string) => {
          const trimmed = (value ?? '').trim()
          if (!trimmed) return 'Vous devez renseigner le nom de la forêt' as string
          return undefined
        }
      }, {
        key: 'communeRef.id',
        name: 'communeRef.id',
        type: 'autoComplete',
        label: 'Commune',
        params: {
          options: communeOptions,
          multiple: false,
          optionsResultLimit: 20,
          disabled: readonly
        },
        onChange: (value) => {
          if (value === undefined) return undefined
          const communeId = typeof value === 'string' ? value : value.key
          const commune = communes.find(c => c.id === communeId)
          if (commune) {
            if (!formState.values.geo?.latitude) {
              formState.setFieldValue('geo.latitude', commune.latitude)
              formState.setFieldValue('geo.longitude', commune.longitude)
            }
            if (!formState.values.department) {
              formState.setFieldValue('department', departments.find(d => d.code === commune.department))
            }
          }
        }
      }, {
        key: 'department',
        name: 'department',
        type: 'autoComplete',
        label: 'Departement',
        params: {
          options: departments,
          getOptionLabel: option => `${option.name} (${option.code})`,
          multiple: false,
          optionsResultLimit: 20,
          isOptionEqualToValue: (option, value) => option.code == value.code,
          disabled: readonly || formState.values.communeRef?.id != undefined
        },
        validator: (_: string, value: Partial<RemovalZoneCreateCommand>) => {
          if (!value.department) return "Vous devez renseigner le département"
          return undefined
        },
      }, {
        key: 'massif',
        name: 'massif',
        type: 'textField',
        label: 'Massif',
        params: {
          disabled: readonly,
        }
      },
      {
        key: 'geo.latitude',
        name: 'geo.latitude',
        type: 'textField',
        label: 'Latitude',
        validator: (_: any, value?: Partial<RemovalZoneCreateCommand>) => {
          if (value?.geo?.latitude === undefined) return undefined
          const lat = Number.parseFloat(`${value.geo.latitude}`)
          if (!(lat >= -90 && lat <= 90)) return "Vous devez renseigner une latitude valide"
          return undefined
        },
        params:  {
          placeholder: "43.602829",
          textFieldType: 'number',
        }
      },
      {
        key: 'geo.longitude',
        name: 'geo.longitude',
        type: 'textField',
        label: 'Longitude',
        validator: (_: any, value?: Partial<RemovalZoneCreateCommand>) => {
          if (value?.geo?.longitude === undefined) return undefined
          const long = Number.parseFloat(`${value.geo.longitude}`)
          if (!(long >= -180 && long <= 180)) return "Vous devez renseigner une longitude valide"
          return undefined
        },
        params: {
          placeholder: "3.879260",
          textFieldType: 'number',
        }
      },
      {
        key: 'organization.id',
        name: 'organization.id',
        type: 'autoComplete',
        label: 'Acteur',
        readOnly: readonlyOrganization,
        params: {
          textFieldProps: {
            placeholder: 'Sélectionner',
          },
          options: getOrganizationRefs.data.map((item) => ({key: item.id, label: item.name})),
          disabled: !service.isMarque()
        },
        validator: undefined,
      },
      {
        key: 'certifications',
        name: 'certifications',
        type: 'autoComplete',
        label: 'Certifications',
        params: {
          options: removalZoneCertifications.map((item) => ({key: item.id, label: item.name})),
          limitTags: 1,
          multiple: true,
          style: {
            flexGrow: 1,
          }
        },
      },
    ],
    [readonly, woodSpecies, communes, communeOptions, departments, getOrganizationRefs.data, removalZoneCertifications]
  )

  return (
    <Stack direction="column" spacing={2}>
      <Stack direction="row" spacing={5}>
        <DropZonePicture {...imageDropZone}/>
        <FormComposable
          fields={detailsFields}
          formState={formState}
        />
      </Stack>
    </Stack>
  )
}
